import { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from 'react';
import { AUDIT_EVENT } from '@/utils/audit';
import { APP_DATA_VERSION_KEY } from '@/lib/apiClient';
import type {
  Appointment, AuditLog, CashMovement, CashRegister, Client, MedicalRecord, Patient,
  PaymentMethodConfig, Product, ProductCategory, Reminder, SaleHistory, StockMovement, Supplier,
} from '@/types/domain';
import {
  fetchProducts,
  fetchClients,
  fetchPatients,
  fetchAppointments,
  fetchCashRegisters,
  fetchCashMovements,
  fetchSales,
  fetchPaymentMethods,
  fetchStockMovements,
  fetchMedicalRecords,
  fetchReminders,
  fetchProductCategories,
  fetchSuppliers,
  updateProductStock,
  upsertClient,
  upsertPatient,
  upsertAppointment,
  upsertReminder,
  createReminder,
  updateReminder,
  sendReminderEmail,
  persistCompleteSaleToAPI,
} from '@/lib/dataService';
import { getProductStockStatus } from '@/utils/productStock';

export type { Appointment, AuditLog, CashMovement, CashRegister, Client, MedicalRecord, Patient, PaymentMethodConfig, Product, ProductCategory, Reminder, SaleHistory, StockMovement, Supplier } from '@/types/domain';

interface AppDataContextType {
  products: Product[];
  setProducts: React.Dispatch<React.SetStateAction<Product[]>>;
  productCategories: ProductCategory[];
  setProductCategories: React.Dispatch<React.SetStateAction<ProductCategory[]>>;
  suppliers: Supplier[];
  setSuppliers: React.Dispatch<React.SetStateAction<Supplier[]>>;
  cashMovements: CashMovement[];
  setCashMovements: React.Dispatch<React.SetStateAction<CashMovement[]>>;
  cashRegisters: CashRegister[];
  setCashRegisters: React.Dispatch<React.SetStateAction<CashRegister[]>>;
  salesHistory: SaleHistory[];
  setSalesHistory: React.Dispatch<React.SetStateAction<SaleHistory[]>>;
  clients: Client[];
  setClients: React.Dispatch<React.SetStateAction<Client[]>>;
  patients: Patient[];
  setPatients: React.Dispatch<React.SetStateAction<Patient[]>>;
  appointments: Appointment[];
  setAppointments: React.Dispatch<React.SetStateAction<Appointment[]>>;
  paymentMethodsConfig: PaymentMethodConfig[];
  setPaymentMethodsConfig: React.Dispatch<React.SetStateAction<PaymentMethodConfig[]>>;
  stockMovements: StockMovement[];
  setStockMovements: React.Dispatch<React.SetStateAction<StockMovement[]>>;
  medicalRecords: MedicalRecord[];
  setMedicalRecords: React.Dispatch<React.SetStateAction<MedicalRecord[]>>;
  auditLogs: AuditLog[];
  setAuditLogs: React.Dispatch<React.SetStateAction<AuditLog[]>>;
  reminders: Reminder[];
  setReminders: React.Dispatch<React.SetStateAction<Reminder[]>>;
  addReminder: (reminder: Reminder) => Promise<void>;
  cancelReminder: (id: string, cancelledBy: string) => void;
  markReminderSent: (id: string) => Promise<void>;
  markReminderError: (id: string, errorMessage: string) => void;
  editReminder: (id: string, subject: string, message: string) => void;
  deductProductStock: (productId: string, quantity: number) => boolean;
  addCashMovement: (movement: CashMovement) => void;
  addSaleToHistory: (sale: SaleHistory) => void;
  addClient: (client: Client) => Promise<void>;
  addPatient: (patient: Patient) => Promise<void>;
  addAppointment: (appointment: Appointment) => Promise<void>;
  persistCompleteSale: (
    sale: SaleHistory,
    items: Array<{
      productId?: string | null;
      productName: string;
      brand: string;
      quantity: number;
      unitPrice: number;
      discountPercentage?: number;
      originalPrice?: number;
      isPriceEdited: boolean;
      priceEditReason?: string;
      subtotal: number;
      isManualProduct?: boolean;
    }>,
    movements: CashMovement[],
    auditEntries: Array<{
      userId: string;
      userName: string;
      action: string;
      entity: string;
      entityId: string;
      details: Record<string, unknown>;
    }>,
    preSaleId?: string,
    accountReceivable?: { initialPayment: number; dueDate?: string; notes?: string },
  ) => Promise<string>;
  isLoading: boolean;
}

const AppDataContext = createContext<AppDataContextType | undefined>(undefined);

export function AppDataProvider({ children }: { children: ReactNode }) {
  const [products, setProducts] = useState<Product[]>([]);
  const [productCategories, setProductCategories] = useState<ProductCategory[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [cashMovements, setCashMovements] = useState<CashMovement[]>([]);
  const [cashRegisters, setCashRegisters] = useState<CashRegister[]>([]);
  const [salesHistory, setSalesHistory] = useState<SaleHistory[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [paymentMethodsConfig, setPaymentMethodsConfig] = useState<PaymentMethodConfig[]>([]);
  const [stockMovements, setStockMovements] = useState<StockMovement[]>([]);
  const [medicalRecords, setMedicalRecords] = useState<MedicalRecord[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const mountedRef = useRef(true);
  const refreshInFlightRef = useRef<Promise<void> | null>(null);
  const lastRefreshAtRef = useRef(0);

  const refreshData = useCallback((showLoading = false) => {
    if (refreshInFlightRef.current) return refreshInFlightRef.current;
    if (showLoading) setIsLoading(true);

    const refreshPromise = (async () => {
      try {
        const [
          dbProducts,
          dbClients,
          dbPatients,
          dbAppointments,
          dbCashRegisters,
          dbCashMovements,
          dbSales,
          dbPaymentMethods,
          dbStockMovements,
          dbMedicalRecords,
          dbReminders,
          dbProductCategories,
          dbSuppliers,
        ] = await Promise.all([
          fetchProducts(),
          fetchClients(),
          fetchPatients(),
          fetchAppointments(),
          fetchCashRegisters(),
          fetchCashMovements(),
          fetchSales(),
          fetchPaymentMethods(),
          fetchStockMovements(),
          fetchMedicalRecords(),
          fetchReminders(),
          fetchProductCategories(),
          fetchSuppliers(),
        ]);

        if (!mountedRef.current) return;
        lastRefreshAtRef.current = Date.now();

        setProducts(dbProducts as Product[]);
        const clients = dbClients as Client[];
        const patients = dbPatients as Patient[];
        const clientNames = new Map(clients.map((client) => [client.id, client.name]));
        const clientPhones = new Map(clients.map((client) => [client.id, client.phone]));
        const enrichedPatients = patients.map((patient) => ({ ...patient, clientName: clientNames.get(patient.clientId) ?? '' }));
        const patientById = new Map(enrichedPatients.map((patient) => [patient.id, patient]));
        setClients(clients.map((client) => ({ ...client, pets: enrichedPatients.filter((patient) => patient.clientId === client.id) })));
        setPatients(enrichedPatients);
        setAppointments((dbAppointments as Appointment[]).map((appointment) => ({
          ...appointment,
          clientName: clientNames.get(appointment.clientId) ?? appointment.clientName ?? '',
          clientPhone: appointment.clientPhone || clientPhones.get(appointment.clientId) || '',
          patientName: patientById.get(appointment.patientId)?.name ?? appointment.patientName ?? '',
          species: patientById.get(appointment.patientId)?.species ?? appointment.species ?? '',
        })));
        setCashRegisters(dbCashRegisters as CashRegister[]);
        setCashMovements(dbCashMovements as CashMovement[]);
        setSalesHistory(dbSales as SaleHistory[]);
        setPaymentMethodsConfig(dbPaymentMethods as PaymentMethodConfig[]);
        setStockMovements(dbStockMovements as StockMovement[]);
        setMedicalRecords(dbMedicalRecords as MedicalRecord[]);
        setReminders(dbReminders as Reminder[]);
        setProductCategories(dbProductCategories as ProductCategory[]);
        setSuppliers(dbSuppliers as Supplier[]);
      } catch (error) {
        console.error('Failed to load application data:', error);
      } finally {
        if (mountedRef.current) setIsLoading(false);
      }
    })().finally(() => {
      refreshInFlightRef.current = null;
    });

    refreshInFlightRef.current = refreshPromise;
    return refreshPromise;
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    void refreshData(true);
    return () => { mountedRef.current = false; };
  }, [refreshData]);

  useEffect(() => {
    let refreshTimer: number | undefined;
    const scheduleRefresh = (onlyIfStale = false) => {
      if (onlyIfStale && Date.now() - lastRefreshAtRef.current < 5 * 60 * 1000) return;
      window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(() => void refreshData(), 350);
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key === APP_DATA_VERSION_KEY) scheduleRefresh();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') scheduleRefresh(true);
    };

    window.addEventListener('storage', onStorage);
    const onFocus = () => scheduleRefresh(true);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.clearTimeout(refreshTimer);
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [refreshData]);

  useEffect(() => {
    const onAudit = (event: Event) => {
      setAuditLogs((prev) => [(event as CustomEvent<AuditLog>).detail, ...prev]);
    };
    window.addEventListener(AUDIT_EVENT, onAudit);
    return () => window.removeEventListener(AUDIT_EVENT, onAudit);
  }, []);

  // Keep the pets shown on each client profile in sync with the canonical
  // patients collection after creates, edits and imports.
  useEffect(() => {
    setClients((previous) => previous.map((client) => ({
      ...client,
      pets: patients.filter((patient) => patient.clientId === client.id),
    })));
  }, [patients]);

  const deductProductStock = useCallback((productId: string, quantity: number): boolean => {
    let found = false;
    setProducts((prev) =>
      prev.map((p) => {
        if (p.id === productId) {
          found = true;
          const newStock = p.stock - quantity;
          const newStatus: Product['status'] = getProductStockStatus(newStock, p.minStock, p.status);
          // Persist to API (fire and forget)
          updateProductStock(productId, newStock, newStatus).catch(() => {});
          return { ...p, stock: newStock, status: newStatus };
        }
        return p;
      })
    );
    return found;
  }, []);

  const addCashMovement = useCallback((movement: CashMovement) => {
    setCashMovements((prev) => [movement, ...prev]);
  }, []);

  const addSaleToHistory = useCallback((sale: SaleHistory) => {
    setSalesHistory((prev) => [sale, ...prev]);
  }, []);

  const addClient = useCallback(async (client: Client) => {
    await upsertClient(client as unknown as Record<string, unknown>);
    setClients((prev) => [client, ...prev]);
  }, []);

  const addPatient = useCallback(async (patient: Patient) => {
    await upsertPatient(patient as unknown as Record<string, unknown>);
    setPatients((prev) => [patient, ...prev]);
  }, []);

  const addAppointment = useCallback(async (appointment: Appointment) => {
    await upsertAppointment(appointment as unknown as Record<string, unknown>);
    setAppointments((prev) => [appointment, ...prev]);
  }, []);

  const addReminder = useCallback(async (reminder: Reminder) => {
    const savedReminder = await createReminder(reminder as unknown as Record<string, unknown>);
    setReminders((prev) =>
      prev.some((current) => current.id === savedReminder.id)
        ? prev.map((current) => current.id === savedReminder.id ? savedReminder : current)
        : [savedReminder, ...prev]
    );
  }, []);

  const cancelReminder = useCallback((id: string, cancelledBy: string) => {
    const cancelledAt = new Date().toISOString();
    setReminders((prev) =>
      prev.map((reminder) =>
        reminder.id === id
          ? { ...reminder, status: 'cancelado', cancelledAt, cancelledBy }
          : reminder
      )
    );
    updateReminder(id, { status: 'cancelado', cancelledAt, cancelledBy }).catch(() => {});
  }, []);

  const markReminderSent = useCallback(async (id: string) => {
    try {
      const sentReminder = await sendReminderEmail(id);
      setReminders((prev) =>
        prev.map((reminder) =>
          reminder.id === id
            ? {
                ...reminder,
                status: sentReminder.status,
                sentAt: sentReminder.sentAt,
                errorMessage: sentReminder.errorMessage,
              }
            : reminder
        )
      );
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'No se pudo enviar el recordatorio.';
      setReminders((prev) =>
        prev.map((reminder) =>
          reminder.id === id ? { ...reminder, status: 'error', errorMessage } : reminder
        )
      );
      throw error;
    }
  }, []);

  const markReminderError = useCallback((id: string, errorMessage: string) => {
    setReminders((prev) =>
      prev.map((reminder) =>
        reminder.id === id ? { ...reminder, status: 'error', errorMessage } : reminder
      )
    );
    updateReminder(id, { status: 'error', errorMessage }).catch(() => {});
  }, []);

  const editReminder = useCallback((id: string, subject: string, message: string) => {
    setReminders((prev) =>
      prev.map((reminder) => (reminder.id === id ? { ...reminder, subject, message } : reminder))
    );
    updateReminder(id, { subject, message }).catch(() => {});
  }, []);

  const persistCompleteSale = useCallback(async (
    sale: SaleHistory,
    items: Array<{
      productId?: string | null;
      productName: string;
      brand: string;
      quantity: number;
      unitPrice: number;
      discountPercentage?: number;
      originalPrice?: number;
      isPriceEdited: boolean;
      priceEditReason?: string;
      subtotal: number;
      isManualProduct?: boolean;
    }>,
    movements: CashMovement[],
    auditEntries: Array<{
      userId: string;
      userName: string;
      action: string;
      entity: string;
      entityId: string;
      details: Record<string, unknown>;
    }>,
    preSaleId?: string,
    accountReceivable?: { initialPayment: number; dueDate?: string; notes?: string },
  ) => {
    try {
      const persistedSale = await persistCompleteSaleToAPI({
        preSaleId,
        referenceNumber: sale.id,
        seller: sale.seller,
        sellerId: sale.sellerId,
        clientId: sale.clientId || null,
        clientName: sale.clientName,
        paymentMethod: sale.paymentMethod,
        discountAmount: Math.max(0, sale.discount - items.reduce((sum, item) => sum + item.unitPrice * item.quantity * Number(item.discountPercentage || 0) / 100, 0)),
        discountPercentage: 0,
        surchargeAmount: sale.surcharge,
        surchargePercentage: 0,
        notes: sale.notes,
        cashRegisterId: movements[0]?.cashRegisterId || undefined,
        cashMovements: movements.map((movement) => ({
          id: movement.id,
          paymentMethod: movement.paymentMethod,
          amount: movement.amount,
        })),
        items: items.map((item) => ({
          productId: item.isManualProduct ? null : item.productId,
          productName: item.productName,
          brand: item.brand,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          discountPercentage: item.discountPercentage || 0,
          subtotal: item.subtotal,
          isManualProduct: item.isManualProduct,
        })),
        accountReceivable,
      });

      void auditEntries;
      if (!persistedSale?.id) {
        throw new Error('La base no devolvió el identificador de la venta.');
      }
      return String(persistedSale.id);
    } catch (err) {
      console.error('Failed to persist sale:', err);
      throw err;
    }
  }, []);

  return (
    <AppDataContext.Provider
      value={{
        products,
        setProducts,
        productCategories,
        setProductCategories,
        suppliers,
        setSuppliers,
        cashMovements,
        setCashMovements,
        cashRegisters,
        setCashRegisters,
        salesHistory,
        setSalesHistory,
        clients,
        setClients,
        patients,
        setPatients,
        appointments,
        setAppointments,
        paymentMethodsConfig,
        setPaymentMethodsConfig,
        stockMovements,
        setStockMovements,
        medicalRecords,
        setMedicalRecords,
        auditLogs,
        setAuditLogs,
        reminders,
        setReminders,
        addReminder,
        cancelReminder,
        markReminderSent,
        markReminderError,
        editReminder,
        deductProductStock,
        addCashMovement,
        addSaleToHistory,
        addClient,
        addPatient,
        addAppointment,
        persistCompleteSale,
        isLoading,
      }}
    >
      {children}
    </AppDataContext.Provider>
  );
}

export function useAppData() {
  const ctx = useContext(AppDataContext);
  if (!ctx) throw new Error('useAppData must be used within AppDataProvider');
  return ctx;
}
